import { useEffect, useState } from "react";
import { Clock, Copy, Headphones, Mail, MessageSquare, Phone, X, AtSign } from "lucide-react";
import { toast } from "sonner";
import { Avatar, Presence } from "./Avatar";
import { useChat } from "@/lib/chat";
import { api } from "@/lib/api";
import { copyText } from "@/lib/format";
import { analytics } from "@/lib/analytics";
import dayjs from "dayjs";

export const ProfilePanel = ({ userId }) => {
  const { usersById, closePanel, openDm, meId, setModal, joinHuddle } = useChat();
  const [detail, setDetail] = useState(null);
  const u = { ...(usersById[userId] || {}), ...(detail || {}) };
  useEffect(() => { api.get(`/users/${userId}`).then((r) => setDetail(r.data)).catch(() => {}); }, [userId]);
  const copy = (v, what) => { copyText(v); analytics.track("email_copied", { category: "email", target: "user", target_id: userId, label: v }); toast.success(`${what} copied`); };
  const isMe = userId === meId;
  return (
    <aside className="tc-panel" data-testid="profile-panel">
      <div className="tc-panel__header">
        <span className="tc-panel__title">Profile</span>
        <button className="tc-iconbtn tc-panel__close" onClick={closePanel} title="Close" data-testid="profile-close-button"><X size={18} /></button>
      </div>
      <div className="tc-panel__body tc-scroll">
        <div className="tc-profile">
          <div className="tc-profile__avatar"><Avatar user={u} size={368} radius={12} style={{ width: "100%", height: "100%" }} /></div>
          <div className="tc-profile__name" data-testid="profile-name">{u.display_name}{u.is_admin && <span className="tc-pill">Admin</span>}{u.is_bot && <span className="tc-msg__app">App</span>}</div>
          {u.real_name && u.real_name !== u.display_name && <div className="tc-profile__title">{u.real_name}</div>}
          {u.title && <div className="tc-profile__title">{u.title}</div>}
          <div className="tc-profile__status"><Presence presence={u.presence} /> {u.presence === "active" ? "Active" : "Away"}{u.status_text ? ` · ${u.status_emoji} ${u.status_text}` : ""}</div>
          <div className="tc-profile__actions">
            {isMe ? (
              <button className="tc-btn tc-btn--outline" style={{ flex: 1 }} onClick={() => setModal({ type: "editProfile" })} data-testid="profile-edit-button">Edit profile</button>
            ) : (
              <>
                <button className="tc-btn tc-btn--outline" style={{ flex: 1 }} onClick={() => openDm([userId])} data-testid="profile-message-button"><MessageSquare size={15} /> Message</button>
                <button className="tc-btn tc-btn--outline" style={{ flex: 1 }} onClick={async () => { const c = await openDm([userId]); joinHuddle(c.id); }} data-testid="profile-huddle-button"><Headphones size={15} /> Huddle</button>
              </>
            )}
          </div>
          <div className="tc-profile__section">
            <h4>Contact information</h4>
            <div className="tc-profile__row">
              <span className="tc-profile__rowicon"><Mail size={16} /></span>
              <div style={{ minWidth: 0 }}>
                <div className="tc-profile__rowlabel">Total Chat email address</div>
                <div className="tc-profile__rowvalue" data-testid="profile-chat-email"><a href={`mailto:${u.chat_email}`}>{u.chat_email}</a><button className="tc-copybtn" title="Copy" onClick={() => copy(u.chat_email, "Email address")} data-testid="profile-copy-chat-email"><Copy /></button></div>
              </div>
            </div>
            {u.email && (
              <div className="tc-profile__row">
                <span className="tc-profile__rowicon"><AtSign size={16} /></span>
                <div style={{ minWidth: 0 }}>
                  <div className="tc-profile__rowlabel">Sign-in email</div>
                  <div className="tc-profile__rowvalue" data-testid="profile-login-email"><a href={`mailto:${u.email}`}>{u.email}</a><button className="tc-copybtn" title="Copy" onClick={() => copy(u.email, "Email address")}><Copy /></button></div>
                </div>
              </div>
            )}
            <div className="tc-profile__row">
              <span className="tc-profile__rowicon"><Phone size={16} /></span>
              <div><div className="tc-profile__rowlabel">Phone</div><div className="tc-profile__rowvalue plain">{u.phone || <span style={{ color: "var(--tc-text-muted)" }}>Not set</span>}</div></div>
            </div>
            <div className="tc-profile__row">
              <span className="tc-profile__rowicon"><Clock size={16} /></span>
              <div><div className="tc-profile__rowlabel">Local time</div><div className="tc-profile__rowvalue plain">{dayjs().format("h:mm A")} {u.timezone ? `(${u.timezone})` : ""}</div></div>
            </div>
          </div>
          <div className="tc-profile__section">
            <h4>About</h4>
            <div className="tc-profile__rowlabel">Handle</div>
            <div className="tc-profile__rowvalue plain" style={{ marginBottom: 10 }}>@{u.handle}</div>
            <div className="tc-profile__rowlabel">Member since</div>
            <div className="tc-profile__rowvalue plain">{u.created_at ? dayjs(u.created_at).format("MMMM D, YYYY") : "—"}</div>
          </div>
        </div>
      </div>
    </aside>
  );
};
